"use client"
import IconChevronDown from "@/app/(site)/_components/Atoms/Icons/IconChevronDown"
import IconChevronUp from "@/app/(site)/_components/Atoms/Icons/IconChevronUp"
import IconMinus from "@/app/(site)/_components/Atoms/Icons/IconMinus"
import IconPlus from "@/app/(site)/_components/Atoms/Icons/IconPlus"
import { useClickOutside } from "@/app/(site)/_utils/_hooks/useOutsideIdentifier"
import { JournalPageType } from "@/types"
import { useRouter } from "next/navigation"
import React, { useEffect, useRef, useState } from "react"
import Text from "../../../Atoms/Text"
import ButtonText from "../../../Molecules/ButtonText"
import RadioButton from "../../../Molecules/Selectors/Radiobutton"
import ArticleGrid from "../Grid/index"
import { filterArticles, sortArticles } from "./helper"

type Props = {
    pageData: JournalPageType | undefined
    params: { locale: string }
}

const JournalGrid: React.FC<Props> = ({ pageData, params }) => {
    const [openSortBy, setOpenSortBy] = useState(false)
    const [openFilter, setOpenFilter] = useState(false)
    const [openMobileFilterAndSort, setOpenMobileFilterAndSort] = useState(false)
    const [selectedSortOption, setSelectedSortOption] = useState<string>("newest")
    const [selectedFilterOption, setSelectedFilterOption] = useState<string>("all")
    const router = useRouter()
    const sortRef = useRef<HTMLDivElement>(null)
    const filterRef = useRef<HTMLDivElement>(null)
    const allCategories =
        pageData?.articleCategories &&
        pageData.articleCategories.map(category => category.categoryTitle.toLowerCase())
    const filterOptions = [...new Set(allCategories)].map(category => ({
        value: category,
        label: category
    }))

    useClickOutside({
        refs: [sortRef, filterRef],
        onOutsideClick: () => {
            setOpenSortBy(false)
            setOpenFilter(false)
        }
    })

    // Set the selected sort and filter options based on the query params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const sortOption = params.get("sort") || "newest"
        const filterOption = params.get("category") || "all"
        setSelectedSortOption(sortOption)
        setSelectedFilterOption(filterOption)
    }, [])

    // Handle sort option selection
    const handleSortOptionSelect = (
        e: React.ChangeEvent<HTMLInputElement>,
        value: string
    ) => {
        e.preventDefault()
        setSelectedSortOption(value)
        const params = new URLSearchParams(window.location.search)
        if (value) {
            params.set("sort", value)
        } else {
            params.delete("sort")
        }
        router.push(`?${params.toString()}`, { scroll: false })
    }

    // Sort and filter articles based on selected options
    const sortedArticles = sortArticles(pageData?.articles, selectedSortOption)

    const filteredArticles = filterArticles(
        sortedArticles,
        selectedFilterOption,
        selectedSortOption
    )

    // Extract the number of articles
    const articleNumber = filteredArticles?.length
    const articleNumberString = `${articleNumber === 1 ? articleNumber + " article" : articleNumber + " articles"} `

    // Handle filter option selection
    function handleFilterOptionSelect(
        e: React.ChangeEvent<HTMLInputElement>,
        value: string
    ) {
        e.preventDefault()
        setSelectedFilterOption(value)
        const params = new URLSearchParams(window.location.search)
        if (value === "all") {
            params.delete("category")
        } else {
            params.set("category", value)
        }
        router.push(`?${params.toString()}`, { scroll: false })
    }

    return (
        <main className="mx-16 md:mx-24" role="main">
            <div className="grid grid-cols-6">
                <div className="col-span-3 flex gap-12 md:col-span-2">
                    <div className="fled md:hidden">
                        <ButtonText
                            size="big"
                            icon={
                                openFilter ? (
                                    <IconMinus className="h-16 w-16" />
                                ) : (
                                    <IconPlus className="h-16 w-16" />
                                )
                            }
                            iconPosition="right"
                            className="flex items-center gap-4 p-14 pl-0"
                            onClick={() => {
                                setOpenMobileFilterAndSort(!openMobileFilterAndSort)
                            }}
                        >
                            FILTER & SORT
                        </ButtonText>
                    </div>
                    <div className="hidden md:flex">
                        <ButtonText
                            size="big"
                            icon={
                                openFilter ? (
                                    <IconMinus className="h-16 w-16" />
                                ) : (
                                    <IconPlus className="h-16 w-16" />
                                )
                            }
                            iconPosition="right"
                            onClick={() => {
                                setOpenFilter(!openFilter)
                                setOpenSortBy(false)
                            }}
                            className="flex items-center gap-4 p-14 pl-0"
                        >
                            BY CATEGORY
                        </ButtonText>
                    </div>
                </div>
                <div className="col-span-3 flex items-center justify-end text-center md:col-span-2 md:justify-center">
                    <Text size="medium" weight="regular" className="text-[#767676]">
                        {articleNumberString}
                    </Text>
                </div>

                <div className="col-span-2 hidden justify-end md:flex">
                    <ButtonText
                        size="big"
                        icon={
                            openSortBy ? (
                                <IconChevronUp className="h-16 w-16" />
                            ) : (
                                <IconChevronDown className="h-16 w-16" />
                            )
                        }
                        onClick={() => {
                            setOpenSortBy(!openSortBy)
                            setOpenFilter(false)
                        }}
                        iconPosition="right"
                        className="flex items-center gap-4 p-14 pl-0"
                    >
                        SORT BY
                    </ButtonText>
                </div>
            </div>
            {openSortBy && (
                <div
                    ref={sortRef}
                    className="absolute right-24 hidden w-300 bg-white p-16 md:flex"
                >
                    <RadioButton
                        name="sort"
                        options={[
                            { value: "newest", label: "Newest" },
                            { value: "oldest", label: "Oldest" },
                            { value: "alphabetical", label: "Alphabetical" }
                        ]}
                        handleSelection={handleSortOptionSelect}
                        selectedValue={selectedSortOption}
                        theme="light"
                    />
                </div>
            )}
            {openFilter && (
                <div
                    ref={filterRef}
                    className="absolute hidden w-300 bg-white p-16 md:flex"
                >
                    <RadioButton
                        name="category"
                        options={[{ value: "all", label: "All" }, ...filterOptions]}
                        handleSelection={handleFilterOptionSelect}
                        selectedValue={selectedFilterOption}
                        theme="light"
                    />
                </div>
            )}
            {openMobileFilterAndSort && (
                <div className="md:hidden">
                    <div className="w-300 bg-white py-16">
                        <Text size="medium" weight="regular" className="mb-16">
                            SORT
                        </Text>

                        <RadioButton
                            name="sort"
                            options={[
                                { value: "newest", label: "Newest" },
                                { value: "oldest", label: "Oldest" },
                                { value: "alphabetical", label: "Alphabetical" }
                            ]}
                            handleSelection={handleSortOptionSelect}
                            selectedValue={selectedSortOption}
                            theme="light"
                        />
                    </div>
                    <div className="w-300 bg-white py-16">
                        <Text size="medium" weight="regular" className="mb-16">
                            FILTER
                        </Text>
                        <RadioButton
                            name="category"
                            options={[{ value: "all", label: "All" }, ...filterOptions]}
                            handleSelection={handleFilterOptionSelect}
                            selectedValue={selectedFilterOption}
                            theme="light"
                        />
                    </div>
                </div>
            )}
            {filteredArticles && (
                <ArticleGrid articles={filteredArticles} locale={params.locale} />
            )}
        </main>
    )
}

export default JournalGrid
